import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import api from "@/lib/api";
import { useSettings, publicCodeUrl } from "@/lib/settings";
import { Printer, ArrowLeft } from "lucide-react";

export default function PrintSheet() {
  const [params] = useSearchParams();
  const { settings } = useSettings();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);

  const idsParam = params.get("ids") || "";

  useEffect(() => {
    const ids = new Set(idsParam.split(",").filter(Boolean));
    if (ids.size === 0) { setLoading(false); return; }
    api.get("/admin/codes")
      .then((r) => setCodes(r.data.filter((c) => ids.has(c.id))))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [idsParam]);

  const origin = settings.public_url || (typeof window !== "undefined" ? window.location.origin : "");

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--vault-bg)" }}>
        <span className="spin" style={{ borderTopColor: "var(--vault-gold)" }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--vault-bg)" }} data-testid="print-sheet">
      {/* Toolbar — hidden on print */}
      <div className="print:hidden sticky top-0 z-10 border-b" style={{ borderColor: "var(--vault-border)", background: "var(--vault-bg)" }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/admin/codes" className="btn-ghost inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm">
            <ArrowLeft className="w-4 h-4" /> Back to codes
          </a>
          <div className="text-sm text-[var(--vault-text-muted)]">
            <span className="font-mono-vault">{codes.length}</span> QR codes ready to print
          </div>
          <button
            onClick={() => window.print()}
            data-testid="print-button"
            className="btn-gold inline-flex items-center gap-2 px-5 py-2 rounded-md text-sm"
          >
            <Printer className="w-4 h-4" /> Print sheet
          </button>
        </div>
      </div>

      {/* Print sheet */}
      <div className="max-w-6xl mx-auto px-6 py-8 print:p-0 print:max-w-full">
        <div className="print:hidden mb-6">
          <p className="overline text-[var(--vault-text-muted)] mb-1">Lot Drop</p>
          <h1 className="font-display text-3xl">Print Sheet</h1>
          <p className="text-sm text-[var(--vault-text-muted)] mt-2">
            Each tile is sized to print 3×4 per A4 page. Use sticker paper or cut along the dotted lines.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4 print:gap-2">
          {codes.map((c) => {
            const url = publicCodeUrl(settings, c.code);
            return (
              <div
                key={c.id}
                className="bg-white text-black rounded-lg p-4 flex flex-col items-center justify-center text-center print:rounded-none print:border print:border-dashed print:border-gray-300 break-inside-avoid"
                style={{ minHeight: "210px" }}
                data-testid={`sheet-tile-${c.code}`}
              >
                <img src={settings.logo_url} alt="" className="w-10 h-10 object-contain mb-2" />
                <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">{settings.brand_name}</p>
                <QRCodeCanvas
                  value={url}
                  size={140}
                  level="H"
                  includeMargin={false}
                  fgColor="#0A0A0C"
                  bgColor="#FFFFFF"
                />
                <p className="mt-3 font-mono text-sm tracking-[0.15em] font-semibold">{c.code}</p>
                {c.label && <p className="text-[10px] text-gray-600 mt-1 truncate max-w-full">{c.label}</p>}
              </div>
            );
          })}
        </div>

        {codes.length === 0 && (
          <div className="text-center py-20 text-[var(--vault-text-muted)]">No codes found for this sheet.</div>
        )}
      </div>

      <style>{`
        @media print {
          body { background: #FFFFFF !important; }
          .print\\:hidden { display: none !important; }
          @page { size: A4; margin: 10mm; }
        }
      `}</style>
    </div>
  );
}
